import React, { useEffect, useRef, useState, useCallback } from 'react'
import anime from 'animejs'
import Panel from './components/Panel.jsx'
import { SettingsModal, ChangelogModal, ResetModal } from './components/Modals.jsx'
import { api } from './api.js'
import * as engine from './sim/engine.js'

const TICK_MS = 1000         // real ms between ticks
const TICK_DELTA = 1         // sim seconds advanced per tick (1 sim-s = 1 month)

export default function App() {
  const [state, setState] = useState(null)
  const [meta, setMeta] = useState({ model: 'claude-opus-4-8', supabase: false, local: false })
  const [playing, setPlaying] = useState(false)
  const [solo, setSolo] = useState(null)
  const [modal, setModal] = useState(null)        // settings | changelog | reset | code
  const [chatOpen, setChatOpen] = useState(true)
  const [input, setInput] = useState('')
  const [toast, setToast] = useState(null)
  const [busy, setBusy] = useState(false)
  const playingRef = useRef(false)
  const localRef = useRef(null)                   // browser-side sim state (fallback)
  const modeRef = useRef('server')                // 'server' | 'local'

  const serverRef = useRef(null)                  // latest server state (stateless API)
  const verRef = useRef(0)                         // bumps on every committed state (guards stale ticks)
  const flash = (message, err = false) => { setToast({ message, err }); setTimeout(() => setToast(null), 4200) }
  const commit = () => { verRef.current++; setState({ ...localRef.current }) }
  const applyServer = (s) => { verRef.current++; serverRef.current = s; setState(s) }

  // Switch to the in-browser engine so the studio runs with no backend.
  const goLocal = useCallback((reason) => {
    modeRef.current = 'local'
    if (!localRef.current) { localRef.current = engine.createState(); engine.tick(localRef.current, 0, true) }
    setMeta(m => ({ ...m, local: true, model: 'local-heuristic (no backend)' }))
    commit()
    if (reason) console.warn('[autoworld] running in local mode:', reason)
  }, [])

  // init: try the backend; if unreachable, run fully in-browser (never hang).
  useEffect(() => {
    (async () => {
      const r = await api.getState()
      if (r.ok) {
        modeRef.current = 'server'
        setMeta({ model: r.model, supabase: r.supabase, local: false })
        const t = await api.tick(r.state, 0, false)
        if (t.ok) applyServer(t.state); else goLocal(t.code || 'tick failed')
      } else {
        goLocal(r.code || 'no backend')
      }
    })()
  }, [goLocal])

  // tick loop
  useEffect(() => {
    playingRef.current = playing
    if (!playing) return
    let alive = true
    const run = async () => {
      while (alive && playingRef.current) {
        if (modeRef.current === 'local') {
          engine.tick(localRef.current, TICK_DELTA, false); commit()
        } else {
          const v = verRef.current
          const r = await api.tick(serverRef.current, TICK_DELTA, false)
          if (v !== verRef.current) { /* a chat/reset landed mid-flight — discard this stale tick */ }
          else if (r.ok) applyServer(r.state)
          else if (r.code === 'network_error') { goLocal('backend dropped'); }  // degrade and keep running
          else flash(r.message, true)
        }
        await new Promise(res => setTimeout(res, TICK_MS))
      }
    }
    run()
    return () => { alive = false }
  }, [playing, goLocal])

  const send = async () => {
    const text = input.trim()
    if (!text || busy) return
    setBusy(true); setInput('')
    if (modeRef.current === 'local') { localChat(text); setBusy(false); animateBubbles(); return }
    const r = await api.chat(serverRef.current, text)
    setBusy(false)
    if (r.ok) { applyServer(r.state); animateBubbles() }
    else if (r.code === 'network_error') { goLocal('backend dropped'); localChat(text); animateBubbles() }
    else flash(r.message, true)
  }

  // in-browser chat: inject the variable, run a forced cycle, post acknowledgements
  const localChat = (text) => {
    const st = localRef.current
    const { targets } = engine.injectVariable(st, text)
    engine.tick(st, 0, true)
    st.chat.unshift({ role: 'human', text, ts: st.monthsElapsed })
    for (const a of st.agents) {
      const adopted = targets.includes(a.window)
      st.chat.unshift({ role: 'agent', window: a.window, adopted,
        text: adopted ? `Adopting it — ${a.thesis}` : `Noted (Window ${targets.join(',')}). Holding: ${a.thesis}` })
    }
    st.chat = st.chat.slice(0, 200)
    commit()
  }

  const reset = async (mode, prompt, title) => {
    setModal(null); setPlaying(false)
    if (modeRef.current === 'local') {
      localRef.current = engine.createState(mode === 'sf' ? engine.defaultProjection() : engine.customProjection({ title, prompt }))
      engine.tick(localRef.current, 0, true); commit(); flash('Timeline reset — workflow preserved.'); return
    }
    const r = await api.reset(mode === 'sf' ? 'sf' : 'custom', prompt, title)
    if (r.ok) { const t = await api.tick(r.state, 0, false); applyServer(t.ok ? t.state : r.state); flash('Timeline reset — workflow preserved.') }
    else if (r.code === 'network_error') { goLocal('backend dropped'); }
    else flash(r.message, true)
  }

  const deploy = async (which) => {
    const r = which === 'github' ? await api.deployGithub() : await api.deployNetlify()
    flash(r.ok ? r.note : r.message, !r.ok)
  }

  const animateBubbles = () => {
    requestAnimationFrame(() => {
      anime({ targets: '.bubble:nth-child(-n+5)', translateY: [14, 0], opacity: [0, 1], duration: 460, easing: 'easeOutCubic', delay: anime.stagger(40) })
    })
  }

  if (!state) return <div className="app"><div className="header glass"><span className="brand">AUTO<b>WORLD</b></span><span className="clock">booting studio…</span></div></div>

  const ind = state.projection.indicators
  const simDate = `${ind.year} · M${(ind.month % 12) + 1}`
  const decade = 2026 + Math.floor(state.monthsElapsed / 12)

  return (
    <div className="app">
      <header className="header glass">
        <span className="brand">AUTO<b>WORLD</b></span>
        <span className="clock"><b>{state.projection.title}</b></span>
        <span className="clock">sim <b>{simDate}</b> · decade <b>{decade}</b></span>
        <span className="spacer" />
        <div className="grp">
          <button className={`btn ${playing ? 'active' : ''}`} onClick={() => setPlaying(p => !p)} title="play/pause display (does not reset)">{playing ? '❚❚ pause' : '▶ play'}</button>
          <button className="btn" onClick={() => setModal('reset')} title="reset timeline / new research">⟲ reset</button>
          <button className="btn" onClick={() => setModal('code')} title="code / preview">{'</>'}</button>
          <button className="btn" onClick={() => setModal('changelog')} title="changelog & audit">▤ log</button>
          <button className="btn" onClick={() => setModal('settings')} title="api keys & settings">⚙ keys</button>
        </div>
      </header>

      <div className="ribbon glass">
        <span className="stat">homeless <b>{Math.round(ind.homelessness?.value || 0).toLocaleString()}</b></span>
        <span className="stat">jobs <b>{(ind.jobs?.value || 0).toFixed(1)}%</b></span>
        <span className="stat">ai-displaced <b>{(ind.aiDisplacement?.value || 0).toFixed(1)}%</b></span>
        <span className="stat">pop <b>{Math.round(ind.population?.value || 0).toLocaleString()}</b></span>
        <span className="stat">housing built <b>{(ind.housingBuilt?.value || 0).toLocaleString()}</b></span>
        <span className="stat">assets <b>{state.asset?.used || 0}/{state.asset?.capacity || 10}</b></span>
        {(state.events || []).slice(0, 3).map((e, i) => <span className="ev" key={i}>◆ {e.rawText.slice(0, 28)}</span>)}
      </div>

      <main className="grid">
        {state.agents.map(a => (
          (!solo || solo === a.window) &&
          <Panel key={a.window} agent={a} solo={solo === a.window}
                 onSolo={() => setSolo(solo === a.window ? null : a.window)}
                 throttle={!!solo && solo !== a.window} />
        ))}
      </main>

      {/* Chat dock (center-bottom-third) — collapsible */}
      {chatOpen && (
        <div className="chatDock">
          <div className="chatHead glass" onClick={() => setChatOpen(false)} title="collapse">
            <span className="title">AGENT <b>CHAT</b></span>
            <span className="hint">@1–@4 targets a window · no tag = all four · others keep it as context</span>
            <span style={{ flex: 1 }} />
            <button className="btn icon" onClick={(e) => { e.stopPropagation(); setChatOpen(false) }} title="collapse">▾</button>
          </div>
          <div className="chatStream">
            {(state.chat || []).slice(0, 14).map((m, i) => (
              <div className={`bubble ${m.role}${m.role === 'agent' ? (m.adopted ? ' adopted' : ' context') : ''}`} key={i}>
                {m.role === 'agent' && <span className="who">WINDOW {m.window}{m.adopted ? ' · adopted' : ' · context'}</span>}
                {m.text}
              </div>
            ))}
          </div>
          <div className="chatInputBar glass">
            <input className="field" value={input} onChange={e => setInput(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && send()}
                   placeholder='inject a variable — e.g. "Major earthquake" or "@1 mayor built 2,000 shelters"' />
            <button className="btn active" onClick={send} disabled={busy}>{busy ? '…' : 'send'}</button>
          </div>
        </div>
      )}
      {!chatOpen && <button className="btn active chatToggle" onClick={() => setChatOpen(true)}>▲ agent chat</button>}

      {modal === 'settings' && <SettingsModal onClose={() => setModal(null)} supabase={meta.supabase} model={meta.model} />}
      {modal === 'changelog' && <ChangelogModal onClose={() => setModal(null)} state={state} />}
      {modal === 'reset' && <ResetModal onClose={() => setModal(null)} onReset={reset} />}
      {modal === 'code' && <CodeView onClose={() => setModal(null)} state={state} meta={meta} />}

      {toast && <div className={`toast glass ${toast.err ? 'err' : ''}`}>{toast.message}</div>}
    </div>
  )
}

function CodeView({ onClose, state, meta }) {
  const view = {
    model: meta.model, supabase: meta.supabase, assetCounts: meta.assetCounts,
    simSeconds: state.simSeconds, monthsElapsed: state.monthsElapsed,
    agents: state.agents.map(a => ({ window: a.window, mood: a.worldState.mood, thesis: a.thesis, divergence: a.divergence, sceneParams: a.worldState }))
  }
  return (
    <div className="overlay" onClick={onClose}>
      <div className="sheet glass" onClick={e => e.stopPropagation()}>
        <h2>State · Code View</h2>
        <div className="sub">Live world parameters driving the four Three.js scenes.</div>
        <pre style={{ fontVariantCaps: 'normal', fontSize: 11, lineHeight: 1.5, overflow: 'auto', maxHeight: '60vh', background: 'rgba(0,0,0,0.4)', padding: 12, borderRadius: 10 }}>
{JSON.stringify(view, null, 2)}
        </pre>
        <div className="row-actions"><button className="btn active" onClick={onClose}>close</button></div>
      </div>
    </div>
  )
}
